import { Platform } from 'react-native';
import type {
  SettingsSliceObject,
  PrinterProfile,
} from '../redux/SettingsSlice';
import type { ThermalPrinterTarget } from '../services/thermalReceiptService';
import { DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE } from '../services/thermalReceiptService';

export type PrinterMode =
  | 'bluetooth'
  | 'usb'
  | 'tcp'
  | 'network'
  | 'web-bluetooth'
  | 'web-serial'
  | 'serial'
  | 'spooler';

export type PersistedPrinter = SettingsSliceObject['settings']['printer'];

/** Draft state shape used by PrinterSettingsScreen `local`. */
export interface PrinterSettingsLocalDraft {
  mode: PrinterMode;
  bluetoothAddress: string;
  ip: string;
  port: string;
  usbVendorId: string;
  usbProductId: string;
  printerName: string;
  bleDeviceId: string;
  bleDeviceName: string;
  bleServiceUuid: string;
  bleCharacteristicUuid: string;
  bleMtu: string;
  bleNamePrefix: string;
  serialBaudRate: string;
  serialDataBits: string;
  serialStopBits: string;
  serialParity: 'none' | 'even' | 'odd';
  serialFlowControl: 'none' | 'hardware';
  serialBufferSize: string;
  serialUsbVendorId: string;
  serialUsbProductId: string;
  serialPath: string;
  autoCut: boolean;
  timeoutMs: string;
  widthMM: string;
  dpi: string;
  charsPerLine: string;
}

/** Exact payload dispatched to Redux / used for dirty comparison. */
export type PrinterSavePayload = PersistedPrinter;

export function effectivePrinterMode(
  mode: PrinterMode,
  platformOS: typeof Platform.OS = Platform.OS,
): PrinterMode {
  if (mode === 'usb' && platformOS !== 'android' && platformOS !== 'web') {
    return 'bluetooth';
  }
  if (mode === 'network' && platformOS !== 'web') return 'tcp';
  if (mode === 'web-bluetooth' && platformOS !== 'web') return 'bluetooth';
  if (mode === 'web-serial' && platformOS !== 'web') return 'bluetooth';
  // serial and spooler are Electron-only (runs within web platform)
  if ((mode === 'serial' || mode === 'spooler') && platformOS !== 'web') {
    return 'tcp';
  }
  return mode;
}

/**
 * Same object shape as `addSingleSetting({ printer })` in PrinterSettingsScreen.save.
 */
export function buildPrinterSavePayload(
  local: PrinterSettingsLocalDraft,
  selectedBleMacs: string[],
  platformOS: typeof Platform.OS = Platform.OS,
): PrinterSavePayload {
  const norm = (value: unknown): string => String(value ?? '').trim();
  const cleanedBleMacs = selectedBleMacs
    .map(s => String(s || '').trim())
    .filter(Boolean);
  return {
    mode: effectivePrinterMode(local.mode, platformOS),
    bluetoothAddresses: cleanedBleMacs.length ? cleanedBleMacs : undefined,
    bluetoothAddress:
      cleanedBleMacs[0] || norm(local.bluetoothAddress) || undefined,
    ip: local.ip,
    port: Number(local.port) || 9100,
    usbVendorId: norm(local.usbVendorId) || undefined,
    usbProductId: norm(local.usbProductId) || undefined,
    printerName: norm(local.printerName) || undefined,
    bleDeviceId: norm(local.bleDeviceId) || undefined,
    bleDeviceName: norm(local.bleDeviceName) || undefined,
    bleServiceUuid: norm(local.bleServiceUuid) || undefined,
    bleCharacteristicUuid: norm(local.bleCharacteristicUuid) || undefined,
    bleMtu: Number(local.bleMtu) || undefined,
    bleNamePrefix: norm(local.bleNamePrefix) || undefined,
    serialBaudRate: Number(local.serialBaudRate) || undefined,
    serialDataBits: parseSerialDataBits(local.serialDataBits),
    serialStopBits: parseSerialStopBits(local.serialStopBits),
    serialParity: local.serialParity || undefined,
    serialFlowControl: local.serialFlowControl || undefined,
    serialBufferSize: Number(local.serialBufferSize) || undefined,
    serialUsbVendorId: norm(local.serialUsbVendorId) || undefined,
    serialUsbProductId: norm(local.serialUsbProductId) || undefined,
    serialPath: norm(local.serialPath) || undefined,
    autoCut: local.autoCut,
    timeoutMs: Number(local.timeoutMs) || 30000,
    widthMM: Number(local.widthMM) || 58,
    dpi: Number(local.dpi) || 203,
    charsPerLine: Number(local.charsPerLine) || 32,
  };
}

/** Map persisted settings to the same comparable shape as a save payload. */
export function reduxPrinterToComparablePayload(
  p: PersistedPrinter,
): PrinterSavePayload {
  return {
    mode: p.mode,
    bluetoothAddresses: p.bluetoothAddresses?.length
      ? p.bluetoothAddresses
      : undefined,
    bluetoothAddress: p.bluetoothAddress,
    ip: p.ip,
    port: p.port ?? 9100,
    usbVendorId: p.usbVendorId,
    usbProductId: p.usbProductId,
    printerName: p.printerName,
    bleDeviceId: p.bleDeviceId,
    bleDeviceName: p.bleDeviceName,
    bleServiceUuid: p.bleServiceUuid,
    bleCharacteristicUuid: p.bleCharacteristicUuid,
    bleMtu: p.bleMtu,
    bleNamePrefix: p.bleNamePrefix,
    serialBaudRate: p.serialBaudRate,
    serialDataBits: p.serialDataBits,
    serialStopBits: p.serialStopBits,
    serialParity: p.serialParity,
    serialFlowControl: p.serialFlowControl,
    serialBufferSize: p.serialBufferSize,
    serialUsbVendorId: p.serialUsbVendorId,
    serialUsbProductId: p.serialUsbProductId,
    serialPath: p.serialPath,
    autoCut: p.autoCut ?? false,
    timeoutMs: p.timeoutMs ?? 30000,
    widthMM: p.widthMM ?? 58,
    dpi: p.dpi ?? 203,
    charsPerLine: p.charsPerLine ?? 32,
  };
}

function bleOrderedListFromPayload(p: PrinterSavePayload): string[] {
  const fromMulti = p.bluetoothAddresses?.length
    ? p.bluetoothAddresses.map(s => String(s || '').trim()).filter(Boolean)
    : [];
  if (fromMulti.length) return fromMulti;
  const one = (p.bluetoothAddress || '').trim();
  return one ? [one] : [];
}

function normUsbId(v: string | undefined): string | undefined {
  const t = (v || '').trim();
  return t || undefined;
}

function parseSerialDataBits(
  value: string | number | undefined,
): 7 | 8 | undefined {
  const n = Number(value);
  return n === 7 || n === 8 ? n : undefined;
}

function parseSerialStopBits(
  value: string | number | undefined,
): 1 | 2 | undefined {
  const n = Number(value);
  return n === 1 || n === 2 ? n : undefined;
}

/**
 * Stable equality for dirty checks (iOS USB mode vs bluetooth, BLE list shapes, etc.).
 */
export function arePrinterSavePayloadsEqual(
  a: PrinterSavePayload,
  b: PrinterSavePayload,
  platformOS: typeof Platform.OS = Platform.OS,
): boolean {
  if (
    effectivePrinterMode(a.mode, platformOS) !==
    effectivePrinterMode(b.mode, platformOS)
  ) {
    return false;
  }
  const bleA = bleOrderedListFromPayload(a);
  const bleB = bleOrderedListFromPayload(b);
  if (bleA.length !== bleB.length) return false;
  for (let i = 0; i < bleA.length; i++) {
    if (bleA[i] !== bleB[i]) return false;
  }
  if (String(a.ip ?? '') !== String(b.ip ?? '')) return false;
  if ((Number(a.port) || 9100) !== (Number(b.port) || 9100)) return false;
  if (normUsbId(a.usbVendorId) !== normUsbId(b.usbVendorId)) return false;
  if (normUsbId(a.usbProductId) !== normUsbId(b.usbProductId)) return false;
  if ((a.printerName || '') !== (b.printerName || '')) return false;
  if ((a.bleDeviceId || '') !== (b.bleDeviceId || '')) return false;
  if ((a.bleDeviceName || '') !== (b.bleDeviceName || '')) return false;
  if ((a.bleServiceUuid || '') !== (b.bleServiceUuid || '')) return false;
  if ((a.bleCharacteristicUuid || '') !== (b.bleCharacteristicUuid || ''))
    return false;
  if ((a.bleMtu ?? 0) !== (b.bleMtu ?? 0)) return false;
  if ((a.bleNamePrefix || '') !== (b.bleNamePrefix || '')) return false;
  if ((Number(a.serialBaudRate) || 0) !== (Number(b.serialBaudRate) || 0))
    return false;
  if ((a.serialDataBits ?? 0) !== (b.serialDataBits ?? 0)) return false;
  if ((a.serialStopBits ?? 0) !== (b.serialStopBits ?? 0)) return false;
  if ((a.serialParity || '') !== (b.serialParity || '')) return false;
  if ((a.serialFlowControl || '') !== (b.serialFlowControl || '')) return false;
  if ((Number(a.serialBufferSize) || 0) !== (Number(b.serialBufferSize) || 0))
    return false;
  if (normUsbId(a.serialUsbVendorId) !== normUsbId(b.serialUsbVendorId))
    return false;
  if (normUsbId(a.serialUsbProductId) !== normUsbId(b.serialUsbProductId))
    return false;
  if ((a.serialPath || '') !== (b.serialPath || '')) return false;
  if (!!a.autoCut !== !!b.autoCut) return false;
  if ((Number(a.timeoutMs) || 30000) !== (Number(b.timeoutMs) || 30000)) {
    return false;
  }
  if ((Number(a.widthMM) || 58) !== (Number(b.widthMM) || 58)) return false;
  if ((Number(a.dpi) || 203) !== (Number(b.dpi) || 203)) return false;
  if ((Number(a.charsPerLine) || 32) !== (Number(b.charsPerLine) || 32)) {
    return false;
  }
  return true;
}

export function isPrinterSettingsDirty(
  local: PrinterSettingsLocalDraft,
  selectedBleMacs: string[],
  persisted: PersistedPrinter | undefined,
  platformOS: typeof Platform.OS = Platform.OS,
): boolean {
  if (!persisted) return true;
  const next = buildPrinterSavePayload(local, selectedBleMacs, platformOS);
  const prev = reduxPrinterToComparablePayload(persisted);
  return !arePrinterSavePayloadsEqual(next, prev, platformOS);
}

// ─── Multi-printer profile utilities ─────────────────────────────────

export function profileToTarget(profile: PrinterProfile): ThermalPrinterTarget {
  switch (profile.mode) {
    case 'bluetooth':
      return { mode: 'bluetooth', macAddress: profile.bluetoothAddress ?? '' };
    case 'tcp':
      return {
        mode: 'tcp',
        host: profile.ip ?? '',
        port: profile.port ?? 9100,
        timeoutMs: profile.timeoutMs,
      };
    case 'usb':
      return {
        mode: 'usb',
        vendorId: profile.usbVendorId ?? '',
        productId: profile.usbProductId ?? '',
      };
    case 'network':
      return { mode: 'network', printerName: profile.printerName ?? '' };
    case 'web-bluetooth': {
      const defaults = DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE;
      return {
        mode: 'web-bluetooth',
        bleServiceUuid: profile.bleServiceUuid || defaults.bleServiceUuid,
        bleCharacteristicUuid:
          profile.bleCharacteristicUuid || defaults.bleCharacteristicUuid,
        bleMtu: profile.bleMtu,
        bleDeviceId: profile.bleDeviceId,
        bleDeviceName: profile.bleDeviceName,
        bleNamePrefix: profile.bleNamePrefix,
      };
    }
    case 'web-serial':
      return {
        mode: 'web-serial',
        serialBaudRate: profile.serialBaudRate ?? 38400,
        serialDataBits: profile.serialDataBits,
        serialStopBits: profile.serialStopBits,
        serialParity: profile.serialParity,
        serialFlowControl: profile.serialFlowControl,
        serialBufferSize: profile.serialBufferSize,
        serialUsbVendorId: profile.serialUsbVendorId,
        serialUsbProductId: profile.serialUsbProductId,
      };
    case 'serial':
      return {
        mode: 'serial',
        path: profile.serialPath ?? '',
        baudRate: profile.serialBaudRate ?? 9600,
      };
    case 'spooler':
      return {
        mode: 'spooler',
        printerName: profile.printerName ?? '',
      };
  }
}

export function profileToLayoutOpts(profile: PrinterProfile) {
  return {
    charsPerLine: profile.charsPerLine ?? 32,
    autoCut: profile.autoCut ?? false,
    widthMM: profile.widthMM ?? 58,
  };
}

export function generateProfileLabel(
  mode: PrinterMode,
  info: {
    bluetoothAddress?: string;
    deviceName?: string;
    ip?: string;
    printerName?: string;
    usbProductName?: string;
    serialLabel?: string;
    serialBaudRate?: number;
    serialPath?: string;
  },
): string {
  switch (mode) {
    case 'bluetooth':
      return info.deviceName || 'Bluetooth Printer';
    case 'tcp':
      return info.ip ? `Network ${info.ip}` : 'Network Printer';
    case 'usb':
      return info.usbProductName || 'USB Printer';
    case 'network':
      return info.printerName || 'QZ Printer';
    case 'web-bluetooth':
      return info.deviceName || 'Web Bluetooth Printer';
    case 'web-serial':
      return info.serialLabel || `Serial ${info.serialBaudRate || 38400} baud`;
    case 'serial':
      return info.serialPath || `Serial ${info.serialBaudRate || 9600} baud`;
    case 'spooler':
      return info.printerName || 'OS Printer';
  }
}

export function isDuplicateProfile(
  existing: PrinterProfile[],
  newProfile: Partial<PrinterProfile>,
): boolean {
  return existing.some(p => {
    if (p.mode !== newProfile.mode) return false;
    switch (p.mode) {
      case 'bluetooth':
        return (
          !!p.bluetoothAddress &&
          p.bluetoothAddress === newProfile.bluetoothAddress
        );
      case 'tcp':
        return p.ip === newProfile.ip && p.port === newProfile.port;
      case 'usb':
        return (
          p.usbVendorId === newProfile.usbVendorId &&
          p.usbProductId === newProfile.usbProductId
        );
      case 'network':
        return p.printerName === newProfile.printerName;
      case 'web-bluetooth':
        return (
          (!!p.bleDeviceId && p.bleDeviceId === newProfile.bleDeviceId) ||
          (!!p.bleDeviceName &&
            p.bleDeviceName === newProfile.bleDeviceName &&
            p.bleServiceUuid === newProfile.bleServiceUuid &&
            p.bleCharacteristicUuid === newProfile.bleCharacteristicUuid)
        );
      case 'web-serial':
        return (
          p.serialBaudRate === newProfile.serialBaudRate &&
          (p.serialUsbVendorId || '') ===
            (newProfile.serialUsbVendorId || '') &&
          (p.serialUsbProductId || '') === (newProfile.serialUsbProductId || '')
        );
      case 'serial':
        return (
          (p.serialPath || '') === (newProfile.serialPath || '') &&
          p.serialBaudRate === newProfile.serialBaudRate
        );
      case 'spooler':
        return p.printerName === newProfile.printerName;
      default:
        return false;
    }
  });
}

export function profileToLegacyPrinter(
  profile: PrinterProfile,
): PersistedPrinter {
  return {
    mode: profile.mode,
    bluetoothAddress: profile.bluetoothAddress,
    bluetoothAddresses: profile.bluetoothAddress
      ? [profile.bluetoothAddress]
      : undefined,
    ip: profile.ip ?? '192.168.100.246',
    port: profile.port ?? 9100,
    usbVendorId: profile.usbVendorId,
    usbProductId: profile.usbProductId,
    printerName: profile.printerName,
    bleDeviceId: profile.bleDeviceId,
    bleDeviceName: profile.bleDeviceName,
    bleServiceUuid: profile.bleServiceUuid,
    bleCharacteristicUuid: profile.bleCharacteristicUuid,
    bleMtu: profile.bleMtu,
    bleNamePrefix: profile.bleNamePrefix,
    serialBaudRate: profile.serialBaudRate,
    serialDataBits: profile.serialDataBits,
    serialStopBits: profile.serialStopBits,
    serialParity: profile.serialParity,
    serialFlowControl: profile.serialFlowControl,
    serialBufferSize: profile.serialBufferSize,
    serialUsbVendorId: profile.serialUsbVendorId,
    serialUsbProductId: profile.serialUsbProductId,
    serialPath: profile.serialPath,
    autoCut: profile.autoCut,
    timeoutMs: profile.timeoutMs ?? 30000,
    widthMM: profile.widthMM,
    dpi: profile.dpi,
    charsPerLine: profile.charsPerLine,
  };
}

export function generateUid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

// ─── Auto-populate helpers ───────────────────────────────────────────

const DEFAULT_PAPER = {
  autoCut: false,
  widthMM: 58,
  dpi: 203,
  charsPerLine: 32,
};

export function autoPopulateBleProfiles(
  bondedDevices: { inner_mac_address?: string; device_name?: string }[],
  existingProfiles: PrinterProfile[],
): PrinterProfile[] {
  const newProfiles: PrinterProfile[] = [];
  for (const device of bondedDevices) {
    const mac = (device.inner_mac_address || '').trim();
    if (!mac) continue;
    if (
      isDuplicateProfile(existingProfiles, {
        mode: 'bluetooth',
        bluetoothAddress: mac,
      })
    )
      continue;
    newProfiles.push({
      id: generateUid(),
      label: device.device_name || 'Bluetooth Printer',
      enabled: true,
      mode: 'bluetooth',
      bluetoothAddress: mac,
      ...DEFAULT_PAPER,
    });
  }
  return newProfiles;
}

export function autoPopulateUsbProfiles(
  usbDevices: {
    vendor_id?: string | number;
    product_id?: string | number;
    product_name?: string;
  }[],
  existingProfiles: PrinterProfile[],
): PrinterProfile[] {
  const newProfiles: PrinterProfile[] = [];
  for (const device of usbDevices) {
    const vid = String(device.vendor_id ?? '').trim();
    const pid = String(device.product_id ?? '').trim();
    if (!vid || !pid) continue;
    if (
      isDuplicateProfile(existingProfiles, {
        mode: 'usb',
        usbVendorId: vid,
        usbProductId: pid,
      })
    )
      continue;
    newProfiles.push({
      id: generateUid(),
      label: device.product_name || 'USB Printer',
      enabled: true,
      mode: 'usb',
      usbVendorId: vid,
      usbProductId: pid,
      ...DEFAULT_PAPER,
    });
  }
  return newProfiles;
}
